import { Amplify, API, Auth, graphqlOperation } from 'aws-amplify';
import { GRAPHQL_AUTH_MODE } from '@aws-amplify/api-graphql'; //NOSONAR
import { ServiceEnrollmentData } from './mutation';
import AbstractConnector from '../abstract-connector';
import { RequestBody, ResponseData } from '../connector';
import { ServiceStatus } from './../../../constants/service.constants';
import awsExports from '../../../../aws-exports';

awsExports.aws_appsync_graphqlEndpoint = process.env
	.GATSBY_AWS_APPSYNC_GRAPHQLENDPOINT as string;
awsExports.aws_cognito_identity_pool_id = process.env
	.GATSBY_AWS_COGNITO_IDENTITY_POOL_ID as string;
Amplify.configure(awsExports);

export abstract class AppsyncConnector extends AbstractConnector {
	protected sessionToken: string | undefined;
	private enrollmentResponse: object | undefined;
	private subscription: ZenObservable.Subscription | null = null;
	private readonly subscriptionData: ServiceEnrollmentData[] = [];
	private isServiceTimers = false;

	// TODO move patientId outside the service enrollment record to avoid the need of this abstract get
	// which requires to refactor the type ResponseData
	protected abstract get patientId(): string | undefined;
	protected abstract getRequestBody(): object;
	protected abstract getServicesFromMutation(response: object): string[];
	protected abstract getDataFromSubscription(
		response: object
	): ServiceEnrollmentData;
	protected abstract get enrollmentQuery(): string;
	protected abstract get subscriptionQuery(): string;

	protected prepareRequest = async (input: RequestBody) => {
		console.log('Preparing request', input);
		try {
			this.sessionToken = await this.authenticate();
			this.subscription = this.subscribe((response: object) => {
				const subscriptionData = this.getDataFromSubscription(response);
				console.timeEnd(subscriptionData.service);
				console.debug('Subscription response', response);
				this.subscriptionData.push(subscriptionData);
				this.handleResponse(this.enrollmentResponse!);
			}, this.subscriptionQuery);
		} catch (err) {
			throw new Error('Error subscribing for results: ' + err);
		}
	};

	protected sendRequest(): Promise<object> {
		console.debug('Sending request');
		return API.graphql({
			...graphqlOperation(this.enrollmentQuery, {
				input: this.getRequestBody(),
			}),
			authMode: GRAPHQL_AUTH_MODE.AWS_IAM,
		});
	}

	protected checkResponse(response: object) {
		this.enrollmentResponse = response;
		const services = this.getServicesFromMutation(response);
		this.setServiceTimers(services);
		let checkResult = false;
		if (services.length) {
			checkResult = services.every((serviceName) => {
				return (
					this.request?.serviceEnrollments.find(
						(requestedService) => requestedService.name === serviceName
					)?.state ||
					this.subscriptionData.find((enrollment) => {
						if (enrollment.state.state !== ServiceStatus.enrolled) {
							throw new Error(serviceName + 'enrollment failed');
						}
						return enrollment.service === serviceName;
					})
				);
			});
		} else {
			throw new Error('Unexpected response received');
		}
		return checkResult;
	}

	protected parseResponse(): ResponseData[] {
		let enrollmentData: ResponseData[] | undefined = [];
		enrollmentData = this.subscriptionData.map((enrollment) => {
			console.debug('Parsing', enrollment.service);
			return {
				patientId: this.patientId,
				service: enrollment.service,
				state: enrollment.state.state,
				card: enrollment.card.items[0] && {
					type: enrollment.service,
					...enrollment.card.items[0],
				},
			};
		});
		return enrollmentData || [];
	}

	protected wrapRequest() {
		console.debug('Wrapping request');
		this.subscription?.unsubscribe();
		this.isServiceTimers = false;
	}

	// Appsync connector methods
	// ------------------------------------------------------------------------

	private subscribe(
		fn: (a: object) => void,
		subscription: string
	): Promise<ZenObservable.Subscription> {
		return API.graphql({
			...graphqlOperation(subscription),
			authMode: GRAPHQL_AUTH_MODE.AWS_IAM,
		}).subscribe({
			next: (data: any) => {
				fn(data.value);
			},
			error: (error: unknown) => {
				console.error(error);
				fn({});
				throw error;
			},
		});
	}

	private async authenticate(): Promise<string> {
		return new Promise<string>((resolve, reject) => {
			Auth.currentCredentials()
				.then((credentials) => {
					credentials.identityId
						? resolve(credentials.identityId)
						: reject(
								new Error(
									'Unauthorized request, no credentials.identityId provided'
								)
						  );
				})
				.catch((reason) => {
					reject(new Error('Unauthorized request: ' + reason));
				});
		});
	}

	private setServiceTimers(services: string[]) {
		if (!this.isServiceTimers) {
			this.isServiceTimers = true;
			services.forEach((service) => console.time(service));
		}
	}
}
